/**
 * TKP-186 md.16 — TASLAK KATMANI: Süreç Yöneticisinin onay açıkken yazdığı kayıtlar taslağa düşer ve onaylanana dek
 * envanterde yoktur; kullanıcı eklediği ilgili kişiyi / işleme kaydını göremiyor, bağımlı özellikleri giremiyordu.
 * Bu yardımcılar kendi bekleyen düzenlemelerimi gerçek listelere "taslak" işaretiyle (`__draft`) katar:
 *  - gölge ilgili kişi satırları (NODE_DATASUBJECT CREATE) → veri kartı ilgili kişi kutusu ve envanter panelleri,
 *  - işleme kümesi (PROCESSING_LEGAL UPDATE, `after.items`), aktarım (TRANSFER CREATE), özellik kayıtları (FEATURE CREATE).
 * Gölge kimlikler gerçek kimlik biçimindedir; backend gölge hedefe yazımı kabul eder ve onayda gerçek kimliğe eşler.
 * TKP-196 ek: bekleyen SİLME (`__draft.removal`) ve GÜNCELLEME (`__draft.update`, `after` değerleri üstüne) mevcut kayıdı
 * işaretler; ilgili kişi satırı seviyesindeki düğüm özellikleri (TKP-195) ve saklama süresi taslağı da katmandadır.
 * Saf: store/Vue yok. Adlar `edit.names` (kimlik → ad) haritasından, yoksa başvurunun kendi `name` alanından.
 */
import { pendingMine } from "./approval";

const id = (v) => String((v && v._id) || v || "");

const nameOf = (names, ref) => (ref ? names[id(ref)] || (typeof ref === "object" ? ref.name : undefined) || null : null);
const ref = (names, value) => (value ? { _id: id(value), name: nameOf(names, value) } : undefined);
/**
 * TKP-241 p.12: işleme kalemlerinin amacı / hukuki sebebi kütüphane bağını (`libraryItem`) ve regülasyonu korur — kayıtlı kalemler
 * kendi `_id`'sini taşımaz, eşleşme bu bağla yapılır. Bağ düşünce değişmeyen kalemler "kaldırılacak" + "taslakta" diye çift listeleniyordu.
 */
const legalRef = (names, value) => {
  if (!value) return undefined;
  if (typeof value !== "object") return ref(names, value);
  return { ...(value._id ? { _id: id(value) } : {}), name: nameOf(names, value._id ? value : null) || value.name || null, ...(value.libraryItem ? { libraryItem: value.libraryItem } : {}), ...(value.regulation ? { regulation: value.regulation } : {}) };
};
const mark = (edit, packageStatus, extra = {}) => ({ shadow: !!edit.shadow, packageStatus, edit: edit._id, ...extra });
const parentOf = (edit) => (edit.parent && edit.parent.entityId ? id(edit.parent.entityId) : "");

/** Düğümün (veri kartı) altına taslağımda eklediğim ilgili kişi satırları — gerçek satır biçiminde. */
export const shadowDatasubjectRowsOf = (drafts, nodeId, accountId) =>
  pendingMine(drafts, accountId)
    .filter(({ edit }) => edit.entityType === "NODE_DATASUBJECT" && edit.operation === "CREATE" && edit.after && (id(edit.after.processData) === id(nodeId) || parentOf(edit) === id(nodeId)))
    .map(({ edit, packageStatus }) => {
      const names = edit.names || {};
      const source = edit.after.datasubject || edit.after.libraryItem;
      return { _id: edit.entityId, processData: id(nodeId), name: nameOf(names, source), datasubject: ref(names, source), feature: {}, __draft: mark(edit, packageStatus) };
    });

/** Birden çok düğüm için gölge satırlar (birim/süreç paneli). */
export const shadowDatasubjectRowsByNode = (drafts, nodeIds = [], accountId) => Object.fromEntries((nodeIds || []).map((n) => [id(n), shadowDatasubjectRowsOf(drafts, n, accountId)]));

/** İlgili kişi satırının taslağımdaki işleme kümesi (kümenin tamamı — formda gerçek kayıtlarla birlikte düzenlenir). */
export const draftProcessingLegalsOf = (drafts, rowId, accountId) =>
  pendingMine(drafts, accountId)
    .filter(({ edit }) => edit.entityType === "PROCESSING_LEGAL" && edit.operation !== "DELETE" && id(edit.entityId) === id(rowId) && edit.after)
    .flatMap(({ edit, packageStatus }) => {
      const names = edit.names || {};
      return (edit.after.items || []).map((i, k) => ({ _id: `${edit._id}-${k}`, purpose: legalRef(names, i.purpose), legalReason: legalRef(names, i.legalReason), legitimateInterest: i.legitimateInterest, note: i.note, __draft: mark(edit, packageStatus) }));
    });

/** İlgili kişi satırına taslağımda eklediğim aktarımlar. */
export const draftTransfersOf = (drafts, rowId, accountId) =>
  pendingMine(drafts, accountId)
    .filter(({ edit }) => edit.entityType === "TRANSFER" && edit.operation === "CREATE" && edit.after && (id(edit.after.datasubject) === id(rowId) || parentOf(edit) === id(rowId)))
    .map(({ edit, packageStatus }) => {
      const names = edit.names || {};
      const a = edit.after;
      return { _id: edit.entityId, datasubject: { _id: id(rowId) }, thirdParty: ref(names, a.thirdParty), legals: (a.legals || []).map((l) => ({ purpose: ref(names, l.purpose), legalReason: ref(names, l.legalReason), legitimateInterest: l.legitimateInterest })), methods: (a.methods || []).map((m) => ref(names, m)).filter(Boolean), preventions: (a.preventions || []).map((m) => ref(names, m)).filter(Boolean), note: a.note, __draft: mark(edit, packageStatus) };
    });

/**
 * Üst kayda (düğüm / ilgili kişi satırı / süreç×kaynak) taslağımda eklediğim özellik kayıtları (`featureKind`: DataDisposalMethod,
 * DataAccessAndRestriction, DatasubjectDatasubjectRight, DatasourcePhysicalStorage…). Ad kütüphane kaleminden; saklama alanları aynen.
 */
/**
 * TKP-254: katman yardımcıları satır başına bütün taslağı tarıyordu (kalabalık birimde 1186 satır × 1186 düzenleme) — dizinler
 * `pendingMine` sonucuna (aynı taslak + kullanıcı için tek dizi) bağlı olarak bir kez kurulur.
 */
const indexCache = new WeakMap();
const indexesOf = (pending) => {
  let idx = indexCache.get(pending);
  if (idx) return idx;
  const features = new Map(); // featureKind → üst kimlik → [{ edit, packageStatus }] (ekleme sırası korunur)
  const changes = new Map(); // entityId → ilk DELETE/UPDATE { edit, packageStatus }
  pending.forEach((entry, order) => {
    const { edit } = entry;
    if (edit.entityType === "FEATURE" && edit.operation === "CREATE" && edit.after) {
      const byParent = features.get(edit.featureKind) || new Map();
      features.set(edit.featureKind, byParent);
      const parents = new Set([parentOf(edit), ...[edit.after.processData, edit.after.datasubject, edit.after.datasource].filter(Boolean).map(id)].filter(Boolean));
      for (const p of parents) byParent.set(p, [...(byParent.get(p) || []), entry]);
    }
    if (edit.operation === "DELETE" || edit.operation === "UPDATE") {
      const key = `${edit.entityType}|${id(edit.entityId)}`;
      if (!changes.has(key)) changes.set(key, { ...entry, order });
    }
  });
  idx = { features, changes, changeTypes: new Set(pending.filter(({ edit }) => edit.operation === "DELETE" || edit.operation === "UPDATE").map(({ edit }) => edit.entityType)) };
  indexCache.set(pending, idx);
  return idx;
};

export const draftFeaturesOf = (drafts, parentId, featureKind, accountId) =>
  ((indexesOf(pendingMine(drafts, accountId)).features.get(featureKind) || new Map()).get(id(parentId)) || [])
    .map(({ edit, packageStatus }) => {
      const names = edit.names || {};
      const a = edit.after;
      const base = { _id: edit.entityId, name: nameOf(names, a.libraryItem) || a.name || null, libraryItem: a.libraryItem ? id(a.libraryItem) : undefined, __draft: mark(edit, packageStatus) };
      if (featureKind === "DataAccessAndRestriction") return { ...base, accessor: ref(names, a.accessor), restriction: ref(names, a.restriction) };
      return { ...base, ...(a.period !== undefined ? { period: a.period } : {}), ...(a.since !== undefined ? { since: a.since } : {}), ...(a.legalBasis !== undefined ? { legalBasis: a.legalBasis } : {}) };
    });

const OWN_KINDS = { directCollectMethods: "DatasourceDirectCollectMethod", otherCollectMethods: "DatasourceOtherCollectMethod", physicalStorages: "DatasourcePhysicalStorage", electronicStorages: "DatasourceElectronicStorage", physicalArchives: "DatasourcePhysicalArchive", electronicArchives: "DatasourceElectronicArchive" };

/**
 * TKP-196 — bekleyen silme / güncelleme: `entityId` listedeki kayda eşitse kayıt `__draft.removal` (silinecek) ya da
 * `__draft.update` (`after` alanları üstüne yazılır; ad kütüphane kaleminden) ile işaretlenir. Liste kopyalanır.
 */
export const markPendingEdits = (items = [], drafts, accountId, entityTypes = ["FEATURE"]) => {
  const { changes, changeTypes } = indexesOf(pendingMine(drafts, accountId));
  if (!entityTypes.some((t) => changeTypes.has(t))) return items || [];
  // eskiden: entityTypes'taki ilk eşleşen düzenleme (taslak sırası) — aynı kimlik farklı türde olmadığından türe göre arama eşdeğer
  const hitOf = (itemId) => entityTypes.map((t) => changes.get(`${t}|${itemId}`)).filter(Boolean).sort((a, b) => a.order - b.order)[0];
  return (items || []).map((item) => {
    if (!item || item.__draft) return item;
    const hit = hitOf(id(item._id));
    if (!hit) return item;
    const { edit, packageStatus } = hit;
    if (edit.operation === "DELETE") return { ...item, __draft: mark(edit, packageStatus, { removal: true }) };
    const names = edit.names || {};
    const a = edit.after || {};
    const patch = {};
    if (a.libraryItem) { patch.libraryItem = id(a.libraryItem); const n = nameOf(names, a.libraryItem); if (n) patch.name = n; }
    if (a.accessor) patch.accessor = ref(names, a.accessor);
    if (a.restriction) patch.restriction = ref(names, a.restriction);
    if (a.thirdParty) patch.thirdParty = ref(names, a.thirdParty);
    for (const k of ["period", "since", "legalBasis", "note"]) if (a[k] !== undefined) patch[k] = a[k];
    if (Array.isArray(a.legals)) patch.legals = a.legals.map((l) => ({ purpose: ref(names, l.purpose), legalReason: ref(names, l.legalReason), legitimateInterest: l.legitimateInterest }));
    return { ...item, ...patch, __draft: mark(edit, packageStatus, { update: true }) };
  });
};

const legalKeyOf = (i) => `${id(i && i.purpose && (i.purpose.libraryItem || i.purpose))}|${id(i && i.legalReason && (i.legalReason.libraryItem || i.legalReason))}`;

/**
 * İşleme kümesi taslağı (PROCESSING_LEGAL UPDATE = kümenin yeniden yazımı): `after.items`te olmayan mevcut kayıt silinecek
 * (`removal`), mevcutta olmayan taslak kalem eklenir, ikisinde de olan aynen kalır. DELETE (tek kayıt) `removal`.
 */
const overlayProcessingLegals = (existing = [], rowId, drafts, accountId) => {
  const mine = pendingMine(drafts, accountId).filter(({ edit }) => edit.entityType === "PROCESSING_LEGAL" && id(edit.entityId) === id(rowId));
  const rewrite = mine.find(({ edit }) => edit.operation !== "DELETE" && edit.after);
  let list = markPendingEdits(existing, drafts, accountId, ["PROCESSING_LEGAL"]);
  const drafted = draftProcessingLegalsOf(drafts, rowId, accountId);
  if (!rewrite) return [...list, ...drafted];
  const afterKeys = new Set(drafted.map(legalKeyOf));
  list = list.map((x) => (x.__draft || afterKeys.has(legalKeyOf(x)) ? x : { ...x, __draft: mark(rewrite.edit, rewrite.packageStatus, { removal: true }) }));
  const existingKeys = new Set((existing || []).map(legalKeyOf));
  return [...list, ...drafted.filter((d) => !existingKeys.has(legalKeyOf(d)))];
};

/**
 * TKP-200 m.17: düğümün taslaktaki tedbir listeleri (NODE güncellemesi) — { administrativeMeasures?, technicalMeasures? } ya da null.
 * Kip (Ekle/Kaldır) tabanı yayımlanmış değil bekleyen değer olmalı; yoksa ikinci "Ekle" ilkinin eklediklerini siler.
 */
export const draftNodeMeasuresOf = (drafts, nodeId, accountId) => {
  const hit = pendingMine(drafts, accountId).find(({ edit }) => edit.entityType === "NODE" && edit.operation === "UPDATE" && id(edit.entityId) === id(nodeId) && edit.after && (edit.after.administrativeMeasures !== undefined || edit.after.technicalMeasures !== undefined));
  if (!hit) return null;
  const a = hit.edit.after;
  return { ...(a.administrativeMeasures !== undefined ? { administrativeMeasures: a.administrativeMeasures } : {}), ...(a.technicalMeasures !== undefined ? { technicalMeasures: a.technicalMeasures } : {}) };
};

/** NODE / NODE_DATASUBJECT güncellemesinde taslak saklama süresi (feature.disposalPeriod) — { value, __draft } ya da null. */
const draftPeriodOf = (drafts, entityType, entityId, accountId) => {
  const hit = pendingMine(drafts, accountId).find(({ edit }) => edit.entityType === entityType && edit.operation === "UPDATE" && id(edit.entityId) === id(entityId) && edit.after && ((edit.after.feature && edit.after.feature.disposalPeriod !== undefined) || edit.after.disposalPeriod !== undefined));
  if (!hit) return null;
  const a = hit.edit.after;
  return { value: a.feature && a.feature.disposalPeriod !== undefined ? a.feature.disposalPeriod : a.disposalPeriod, __draft: mark(hit.edit, hit.packageStatus, { update: true }) };
};

/**
 * İlgili kişi envanteri (satır kimliği → {processingLegals, transferings, datasubjectRights, (TKP-195) disposalMethods,
 * accessAndRestrictions, own, disposalPeriod}) + taslaklarım (ekleme, silme, güncelleme). Girdi değişmez.
 */
export const overlayDatasubjectInventory = (inventory = {}, rows = [], drafts, accountId) => {
  const out = { ...(inventory || {}) };
  if (!pendingMine(drafts, accountId).length) return out;
  for (const row of rows || []) {
    const key = id(row);
    const base = out[key] || {};
    const legals = overlayProcessingLegals(base.processingLegals || [], key, drafts, accountId);
    const transfers = [...markPendingEdits(base.transferings || [], drafts, accountId, ["TRANSFER"]), ...draftTransfersOf(drafts, key, accountId)];
    const rights = [...markPendingEdits(base.datasubjectRights || [], drafts, accountId), ...draftFeaturesOf(drafts, key, "DatasubjectDatasubjectRight", accountId)];
    // TKP-195: satır seviyesindeki düğüm özellikleri
    const disposal = [...markPendingEdits(base.disposalMethods || [], drafts, accountId), ...draftFeaturesOf(drafts, key, "DataDisposalMethod", accountId)];
    const access = [...markPendingEdits(base.accessAndRestrictions || [], drafts, accountId), ...draftFeaturesOf(drafts, key, "DataAccessAndRestriction", accountId)];
    const own = {};
    for (const [field, kind] of Object.entries(OWN_KINDS)) {
      const list = [...markPendingEdits((base.own || {})[field] || [], drafts, accountId), ...draftFeaturesOf(drafts, key, kind, accountId)];
      if (list.length) own[field] = list;
    }
    const period = draftPeriodOf(drafts, "NODE_DATASUBJECT", key, accountId);
    const touched = (list) => (list || []).some((x) => x && x.__draft);
    if (!period && !touched(legals) && !touched(transfers) && !touched(rights) && !touched(disposal) && !touched(access) && !Object.values(own).some(touched)) continue; // taslağı olmayan satır dokunulmaz
    out[key] = { ...base, processingLegals: legals, transferings: transfers, datasubjectRights: rights, disposalMethods: disposal, accessAndRestrictions: access, own: { ...(base.own || {}), ...own }, ...(period ? { disposalPeriod: period.value, disposalPeriodDraft: period.__draft } : {}) };
  }
  return out;
};

/** Düğüm envanteri (düğüm kimliği → {disposalMethods, accessAndRestrictions, own:{…}, disposalPeriod}) + taslaklarım (ekleme, silme, güncelleme, süre). Girdi değişmez. */
export const overlayNodeInventory = (inventory = {}, nodeIds = [], drafts, accountId) => {
  const out = { ...(inventory || {}) };
  if (!pendingMine(drafts, accountId).length) return out;
  for (const n of nodeIds || []) {
    const key = id(n);
    const base = out[key] || { disposalPeriod: 0, disposalMethods: [], accessAndRestrictions: [], own: {}, datasources: {} };
    const disposal = [...markPendingEdits(base.disposalMethods || [], drafts, accountId), ...draftFeaturesOf(drafts, key, "DataDisposalMethod", accountId)];
    const access = [...markPendingEdits(base.accessAndRestrictions || [], drafts, accountId), ...draftFeaturesOf(drafts, key, "DataAccessAndRestriction", accountId)];
    const own = {};
    for (const [field, kind] of Object.entries(OWN_KINDS)) {
      const list = [...markPendingEdits((base.own || {})[field] || [], drafts, accountId), ...draftFeaturesOf(drafts, key, kind, accountId)];
      if (list.length) own[field] = list;
    }
    const period = draftPeriodOf(drafts, "NODE", key, accountId);
    const touched = (list) => (list || []).some((x) => x && x.__draft);
    if (!period && !touched(disposal) && !touched(access) && !Object.values(own).some(touched)) continue; // taslağı olmayan düğüm dokunulmaz
    out[key] = { ...base, disposalMethods: disposal, accessAndRestrictions: access, own: { ...(base.own || {}), ...own }, ...(period ? { disposalPeriod: period.value, disposalPeriodDraft: period.__draft } : {}) };
  }
  return out;
};

/**
 * TKP-189 — taslağımdaki yeni BİRİM ve SÜREÇ kartları (Varlıklar kutuları): kullanıcı eklediği birimin/sürecin altına süreç /
 * veri / ilgili kişi ekleyebilsin diye gölge kart olarak çizilir ve seçilebilir (üst kimlik gölge kimliktir; taslak kapısı
 * gölge üst kaydı tanır, onayda zincir sırayla yazılır). Yalnız kendi bekleyen düzenlemelerim; başkasının taslağı görünmez.
 */
export const shadowDepartmentsOf = (drafts, accountId) =>
  pendingMine(drafts, accountId)
    .filter(({ edit }) => edit.entityType === "DEPARTMENT" && edit.operation === "CREATE" && edit.shadow && edit.after)
    .map(({ edit, packageStatus }) => {
      const names = edit.names || {};
      const after = edit.after;
      return { _id: edit.entityId, name: after.name || null, parent: after.parent ? ref(names, after.parent) : null, processAllowed: after.processAllowed !== false, __draft: mark(edit, packageStatus) };
    });

export const shadowProcessesOf = (drafts, department, accountId) =>
  pendingMine(drafts, accountId)
    .filter(({ edit }) => edit.entityType === "PROCESS" && edit.operation === "CREATE" && edit.shadow && edit.after && (id(edit.after.department) === id(department) || parentOf(edit) === id(department)))
    .map(({ edit, packageStatus }) => {
      const after = edit.after;
      const names = edit.names || {};
      // birim başvurusu adıyla (seçili birim nesnesi, ad haritası ya da taslaktaki ad) — alt kutuların başlığı okur
      const departmentName = (department && typeof department === "object" && department.name) || names[id(department)] || (after.department && after.department.name) || null;
      return { _id: edit.entityId, name: after.name || (after.libraryItem && after.libraryItem.name) || null, department: { _id: id(department), name: departmentName }, fromLibrary: !!after.fromLibrary, admins: [], __draft: mark(edit, packageStatus) };
    });

export const isDraftItem = (item) => !!(item && item.__draft);
/** Rozet: bekleyen silme → "kaldırılacak", güncelleme → "güncellenecek"; aksi halde taslakta / onayda. */
export const draftBadgeKey = (item) => {
  const d = item && item.__draft;
  if (!d) return "approval.badge.draft";
  if (d.removal) return d.packageStatus === "SUBMITTED" ? "approval.badge.removal-submitted" : "approval.badge.removal";
  if (d.update) return d.packageStatus === "SUBMITTED" ? "approval.badge.update-submitted" : "approval.badge.update";
  return d.packageStatus === "SUBMITTED" ? "approval.badge.submitted" : "approval.badge.draft";
};
export const isPendingRemoval = (item) => !!(item && item.__draft && item.__draft.removal);

/** İşleme kümesi düzenleme formuna giderken taslak kalemlerin yapay kimliği ve işareti düşer (küme yeniden yazılır). */
export const editableLegals = (items = []) => (items || []).map((i) => (i && i.__draft ? Object.fromEntries(Object.entries(i).filter(([k]) => k !== "_id" && k !== "__draft")) : i));

/** Birim/süreç hiyerarşisine (birim → süreç → düğüm → ilgili kişi satırları) taslağımdaki gölge satırları ekler. Girdi değişmez. */
export const withShadowRows = (departments = [], drafts, accountId) =>
  (departments || []).map((department) => ({
    ...department,
    processes: (department.processes || []).map((process) => ({
      ...process,
      processDatas: (process.processDatas || []).map((node) => {
        const shadows = shadowDatasubjectRowsOf(drafts, node._id, accountId);
        return shadows.length ? { ...node, datasubjects: [...(node.datasubjects || []), ...shadows] } : node;
      }),
    })),
  }));

/**
 * TKP-200 m.19 — taslakta açtığım SÜREÇ ve VERİ DÜĞÜMLERİ (gölge) envanter özellikleri paneline katılır: gölge süreç kutusunda ve
 * gölge veri kutusunda da panel açılır, özellikler gölge kimliğe taslak olarak yazılır (kapı en yakın gerçek ataya göre yetki verir).
 * `department` paneldeki birim; ağaçta yoksa eklenir. Gerçek düğümlere dokunulmaz.
 */
export const withShadowNodes = (departments = [], drafts, accountId, department) => {
  const mine = pendingMine(drafts, accountId);
  const shadowNodes = mine.filter(({ edit }) => edit.entityType === "NODE" && edit.operation === "CREATE" && edit.shadow && edit.after);
  const shadowProcs = mine.filter(({ edit }) => edit.entityType === "PROCESS" && edit.operation === "CREATE" && edit.shadow && edit.after);
  if (!shadowNodes.length && !shadowProcs.length) return departments || [];
  const nodeOfEdit = ({ edit, packageStatus }) => {
    const names = edit.names || {};
    const source = edit.after.data || edit.after.libraryItem;
    return { _id: edit.entityId, process: id(edit.after.process) || parentOf(edit), data: { _id: id(edit.after.data) || undefined, name: nameOf(names, source) }, datasubjects: [], __draft: mark(edit, packageStatus) };
  };
  const nodesOf = (processId) => shadowNodes.filter(({ edit }) => (id(edit.after.process) || parentOf(edit)) === id(processId)).map(nodeOfEdit);
  const depId = department ? id(department) : "";
  let tree = (departments || []).map((d) => ({ ...d, processes: (d.processes || []).map((p) => { const extra = nodesOf(p._id); return extra.length ? { ...p, processDatas: [...(p.processDatas || []), ...extra] } : p; }) }));
  if (depId && !tree.some((d) => id(d) === depId)) tree = [...tree, { ...(typeof department === "object" ? department : { _id: depId }), processes: [] }];
  return tree.map((d) => {
    const own = shadowProcs.filter(({ edit }) => (id(edit.after.department) || parentOf(edit)) === id(d)).filter(({ edit }) => !(d.processes || []).some((p) => id(p) === id(edit.entityId)));
    if (!own.length) return d;
    const procs = own.map(({ edit, packageStatus }) => ({ _id: edit.entityId, name: edit.after.name || (edit.after.libraryItem && edit.after.libraryItem.name) || null, datasources: [], processDatas: nodesOf(edit.entityId), __draft: mark(edit, packageStatus) }));
    return { ...d, processes: [...(d.processes || []), ...procs] };
  });
};

